import { useEffect, useRef, useState } from "react";
import type {
	ClientToHostMessage,
	MessagePayload,
} from "@rubyplay/effw-connector";
import type { HostSessionState } from "@/hooks/useHostSession";
import type { ProviderRuntimeHookState } from "@/hooks/useProviderRuntime";
import { useCommunicator } from "@/contexts/CommunicatorContext";
import { debugLog } from "@/utils";

type StartupStatusPayload = MessagePayload<
	ClientToHostMessage,
	"ClientStartupLoadingStatus"
>;

type StartupAssetPreloadSnapshot = {
	started: boolean;
	progress: number;
	ready: boolean;
};

/**
 * Reports EF's integration-agnostic startup loading status to the host.
 *
 * noWork: EF has nothing that should participate in host startup loading.
 * loading: EF has startup work and the host may wait until ready, failed, or timeout.
 * ready: EF startup work completed and integration can be used immediately.
 * failed: EF startup work failed or became unavailable; the host should continue without blocking.
 */
export function useClientStartupLoadingReporter(
	host: HostSessionState,
	providerRuntime: ProviderRuntimeHookState
): void {
	const { sendAssetLoadingProgress, sendClientStartupLoadingStatus } = useCommunicator();
	const lastPayloadKeyRef = useRef<string | null>(null);
	const [startupAssetPreload, setStartupAssetPreload] = useState<StartupAssetPreloadSnapshot>({
		started: false,
		progress: 0,
		ready: false,
	});

	useEffect(() => {
		const provider = providerRuntime.provider;
		if (!provider || !providerRuntime.providerReady || provider.capabilities?.supportsStartupAssets !== true) {
			setStartupAssetPreload({
				started: false,
				progress: 0,
				ready: false,
			});
			return;
		}

		return provider.subscribe((event) => {
			if (event.type !== "startup_assets_progress") {
				return;
			}

			setStartupAssetPreload((previous) => ({
				started: true,
				progress: Math.max(previous.progress, event.progress),
				ready: previous.ready || event.progress >= 1,
			}));
		});
	}, [host.hostStartupPreloadRequested, providerRuntime.provider, providerRuntime.providerReady]);

	useEffect(() => {
		if (!host.hostStartupPreloadRequested) {
			return;
		}

		const snapshot = providerRuntime.providerRuntimeSnapshot;
		let payload: StartupStatusPayload;

		if (!host.hostConfigured) {
			payload = {
				state: "loading",
				progress: 0,
				reason: "host_configuration_pending",
			};
		} else if (!host.providerBootEligible || !host.providerBootInput?.integrationType) {
			payload = {
				state: "noWork",
				progress: 1,
				reason: "provider_boot_not_eligible",
			};
		} else if (providerRuntime.providerReady || snapshot.phase === "ready") {
			if (
				providerRuntime.provider?.capabilities?.supportsStartupAssets === true &&
				!startupAssetPreload.ready
			) {
				payload = {
					state: "loading",
					progress: startupAssetPreload.progress,
					reason: startupAssetPreload.started
						? "startup_asset_preload_in_progress"
						: "startup_asset_preload_pending",
				};
			} else {
				payload = {
					state: "ready",
					progress: 1,
				};
			}
		} else if (snapshot.phase === "failed") {
			payload = {
				state: "failed",
				progress: 1,
				reason: snapshot.error.message,
			};
		} else if (snapshot.phase === "degraded") {
			payload = {
				state: "failed",
				progress: 1,
				reason: snapshot.reason,
			};
		} else if (providerRuntime.inFlightBootGeneration !== null || snapshot.phase === "booting" || snapshot.phase === "eligible") {
			payload = {
				state: "loading",
				progress: 0,
			};
		} else {
			payload = {
				state: "loading",
				progress: 0,
				reason: "provider_runtime_pending",
			};
		}

		const payloadKey = JSON.stringify(payload);
		if (lastPayloadKeyRef.current === payloadKey) {
			return;
		}

		lastPayloadKeyRef.current = payloadKey;

		debugLog("[EF StartupLoading] send status", payload);
		sendClientStartupLoadingStatus(payload);

		if (payload.progress !== undefined) {
			sendAssetLoadingProgress({
				total: 1,
				preFetched: payload.progress,
				progress: payload.progress,
			});
		}
	}, [
		host.hostConfigured,
		host.hostStartupPreloadRequested,
		host.providerBootEligible,
		host.providerBootInput?.integrationType,
		providerRuntime.inFlightBootGeneration,
		providerRuntime.provider,
		providerRuntime.providerReady,
		providerRuntime.providerRuntimeSnapshot,
		sendAssetLoadingProgress,
		sendClientStartupLoadingStatus,
		startupAssetPreload,
	]);
}
